'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submissionService } from '@/lib/services/submissionService';
import { createClient } from '@/lib/supabase/client';

export default function AIMarketingEnquiryForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    course: 'AI Powered Digital Marketing',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await submissionService.createLeadSubmission(formData);
      
      if (result) {
        try {
          const supabase = createClient();
          await supabase.functions.invoke('send-lead-notification', {
            body: {
              name: formData.name,
              phone: formData.phone,
              email: formData.email,
              course: formData.course,
            },
          });
        } catch (emailError) {
          console.log('Email notification error:', emailError);
        }

        setIsSuccess(true);
        setSubmitMessage('🎉 Thank you for your interest! You\'ve unlocked 10% OFF on admission. Our team will contact you within 24 hours to schedule your free demo lecture and counselling session.');
        setFormData({ name: '', phone: '', email: '', course: 'AI Powered Digital Marketing' });
      } else {
        setSubmitMessage('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('AI Marketing enquiry submission error:', error);
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-border rounded-3xl p-8 md:p-12 shadow-xl">
      {isSuccess ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={56} className="text-green-600" />
          </div>
          <h3 className="font-display text-3xl uppercase tracking-tight text-foreground mb-4">
            Enquiry Submitted!
          </h3>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            {submitMessage}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 max-w-md mx-auto">
            <p className="text-sm text-yellow-800 font-semibold">
              📞 Need immediate assistance? Call us at <a href="tel:9763150150" className="underline">9763150150</a>
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              pattern="[0-9]{10}"
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Enter 10-digit phone number"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              placeholder="Enter your email address"
            />
          </div>

          <div className="bg-primary/5 rounded-xl p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="GiftIcon" size={20} className="text-primary" />
              What You Get:
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">10% Discount</span> on admission fees</span>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Free Demo Lecture</span> to experience our teaching</span>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Free Career Counselling</span> from industry experts</span>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Course Curriculum</span> detailed breakdown</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white py-4 rounded-xl font-bold uppercase tracking-wider hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Submit Enquiry & Get 10% OFF
                <Icon name="ArrowRightIcon" size={20} />
              </>
            )}
          </button>

          {submitMessage && !isSuccess && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{submitMessage}</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center">
            By submitting this form, you agree to be contacted by Apollobits Academy regarding your enquiry.
          </p>
        </form>
      )}
    </div>
  );
}